import { deleteHttp, post, put, putNoBody } from "../../../common/api/apiShared";
import { ProcessingActivityModel, SelectableAgreement, SelectableDataCategoryItem, SelectableLegalUnitsItem } from "../../ProcessingActivity.types";
import { useTranslation } from "../../../localization/useTranslation";
import { ValidationError } from "../../../common/validationError";
import { useSelectableLegalEntityQuery } from "../../../legalEntity/useSelectableLegalEntityQuery";
import { useSelectableDataCategoriesQuery } from "../../hooks/useSelectableDataCategoriesQuery";
import { useSelectableDocumentsQuery } from "../../hooks/useSelectableDocumentQuery";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { UserSelectableItem } from "../../../user/User.types";
import { LevelOfProtectionType } from "../../../legalEntity/addLegalEntityDialog/AddLegalEntityDialog.types";
import { ProcessingActivityDisclosuresPermissions } from "../../../auth/userContextProvider/UserContextProvider.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useSelectableAllLegalBasesAllQuery, useTransferBasesQuery } from "../../../common/hooks/useSelectableItemQueries";
import { useState } from "react";
import { AgreementSaveModel, DataProcessorAgreementSaveModel } from "../agreementDialog/DataProcessorAgreementDialog.types";
import { useExpandedId } from "../../../common/hooks/useExpandedId";
import { useOptimisticUpdate } from "../../../common/hooks/useOptimisticUpdate";
import { IDisclosureSubStep } from "./DisclosureStep";
import { useGroupEntitiesQuery } from "../../hooks/useGroupEntitiesQuery.ts";
import { ContractOnTransfer, SelectableContract, toSelectableAgreement } from "../../../contract/contract/Contract.types.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { AddToAllDataCategoriesDialogHooks } from "../../addToAllDataCategoriesDialog/AddToAllDataCategoriesDialog.hooks.ts";
import { AddToAllDialogType } from "../../addToAllDataCategoriesDialog/AddToAllDataCategoriesDialog.types";

export function useDisclosureSubStep(props: IDisclosureSubStep) {
    const { permissions } = useUserContext();
    const [dataProcessorAgreementSaveModel, setDataProcessorAgreementSaveModel] = useState<DataProcessorAgreementSaveModel | undefined>(undefined);
    const [transferId, setTransferId] = useState("");
    const { getExpandedId, setExpandedId } = useExpandedId();
    const optimisticUpdate = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();
    const addToAllDialog = AddToAllDataCategoriesDialogHooks();

    const legalEntityIdOnTransfer = props.processingActivity.disclosures.find((x) => x.id === getExpandedId())?.legalUnitId ?? undefined;

    const contractsOnDisclosureUrl = `/ProcessingActivity/Disclosure/${getExpandedId()}/contracts`;
    const contractsOnDisclosureQuery = useDotLegalQuery<ContractOnTransfer>(contractsOnDisclosureUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer,
    });
    const selectableContractsUrl = `/LegalEntity/${legalEntityIdOnTransfer}/selectableContracts`;
    const selectableContractsQuery = useDotLegalQuery<Array<SelectableContract>>(selectableContractsUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer,
    });

    let queryKey = "processingActivity" + props.processingActivity.id;

    const disclosurePermissions = permissions.processingActivityDisclosuresPermissions;

    let dataCategoriesQuery = useSelectableDataCategoriesQuery();
    let legalEntitiesQuery = useSelectableLegalEntityQuery();
    let responsibleQuery = useResponsibleQuery(disclosurePermissions.hasResponsiblePermission);
    let groupEntityQuery = useGroupEntitiesQuery();
    let transferBasisQuery = useTransferBasesQuery(disclosurePermissions.hasTransferBasisPermission);
    let legalbasisQuery = useSelectableAllLegalBasesAllQuery(disclosurePermissions.hasBasisOfDisclosurePermission);
    let documentsQuery = useSelectableDocumentsQuery(
        disclosurePermissions.hasAgreementPermission,
        props.transfers.find((x) => x.id === getExpandedId())?.legalUnitId ?? undefined,
        props.processingActivity.id
    );

    let dataCategoryLoading = dataCategoriesQuery.isLoading;
    let legalEntityLoading = legalEntitiesQuery.isLoading;
    let groupEntityLoading = groupEntityQuery.isLoading;
    let responsibleLoading = responsibleQuery.isLoading;
    let transferBasisLoading = transferBasisQuery.isLoading;
    let legalBasesLoading = legalbasisQuery.isLoading;
    let documentsLoading = documentsQuery.isLoading;

    let legalEntityData = legalEntitiesQuery.data;
    let groupEntityData = groupEntityQuery.allGroupEntities;
    let transferBasisData = transferBasisQuery.data;
    let legalBasesData = legalbasisQuery.data;
    let selectableDocuments: Array<SelectableAgreement> = documentsQuery.disclosureDocuments.map((d) => {
        return {
            id: d.id,
            name: d.name,
            contractOrDocumentType: d.documentTypeName,
            status: d.status,
            type: "document",
        };
    });

    const selectableContracts: Array<SelectableAgreement> = selectableContractsQuery.data?.map((c) => toSelectableAgreement(c)) ?? [];

    let dataCategoriesData = Array<SelectableDataCategoryItem>();
    if (dataCategoriesQuery.allDataCategories) {
        dataCategoriesData = dataCategoriesQuery.allDataCategories!.filter(function (item) {
            return props.dataCategories
                .map((d) => {
                    return d!;
                })
                .includes(item.id);
        });
    }

    const removeAgreementMutation = useDotLegalMutation(removeAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });
    const addAgreementMutation = useDotLegalMutation(addAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });

    const onAddAgreement = async (agreement: SelectableAgreement, disclosureId: string) => {
        if (agreement.type === "document") {
            const agreementId = agreement.id;
            const newProcessingActivityModel = { ...props.processingActivity };
            const source = newProcessingActivityModel.disclosures.find((d) => d.id === disclosureId)!;

            source.transferAgreements.push(agreementId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementMutation.mutateAsync, {
                processingActivityId: props.processingActivity.id,
                disclosureId: disclosureId,
                saveModel: { agreementId: agreementId },
            });
        } else if (agreement.type === "contract") {
            const contractId = agreement.id;
            const contractsOnTransfer = contractsOnDisclosureQuery.data!;
            contractsOnTransfer.contractIds.push(contractId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnDisclosureUrl, addContractApi, {
                disclosureId: getExpandedId(),
                contractId: contractId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onDeleteAgreement = async (agreement: SelectableAgreement, disclosureId: string) => {
        if (agreement.type === "document") {
            const agreementId = agreement.id;
            const newProcessingActivityModel = { ...props.processingActivity };
            const source = newProcessingActivityModel.disclosures.find((d) => d.id === disclosureId)!;

            source.transferAgreements = source.transferAgreements.filter((x) => x !== agreementId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, removeAgreementMutation.mutateAsync, {
                processingActivityId: props.processingActivity.id,
                disclosureId: disclosureId,
                saveModel: { agreementId: agreementId },
            });
        } else if (agreement.type === "contract") {
            const contractId = agreement.id;
            const contractsOnTransfer = contractsOnDisclosureQuery.data!;
            contractsOnTransfer.contractIds = contractsOnTransfer.contractIds.filter((x) => x !== contractId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnDisclosureUrl, removeContractApi, {
                disclosureId: getExpandedId(),
                contractId: contractId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onHasAgreementChange = async (hasAgreement: boolean, disclosureId: string) => {
        const newProcessingActivityModel = { ...props.processingActivity };
        const source = newProcessingActivityModel.disclosures.find((d) => d.id === disclosureId)!;

        source.hasDataProcessingAgreement = hasAgreement;
        source.transferAgreements = [];

        await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementMutation.mutateAsync, {
            processingActivityId: props.processingActivity.id,
            disclosureId: disclosureId,
            saveModel: { hasAgreement: hasAgreement },
        });
    };

    const onSaveNationalLaw = (disclosureId: string, response: any, dataCategoryId: string) => {
        const dc = props.transfers.find((x) => x.id! === disclosureId)!.dataCategories.find((dc) => dc.dataCategoryId === dataCategoryId)!;
        dc.nationalLawId = response.id;
        props.onDisclosureUpdate(dc, disclosureId);

        legalbasisQuery.refetch();
    };

    const onLegalEntityOnTransferChange = () => {
        if (contractsOnDisclosureQuery.data) {
            const newData = { ...contractsOnDisclosureQuery.data };
            newData.contractIds = [];
            queryClient.setQueryData(contractsOnDisclosureUrl, newData);
        }
    };

    const showAddToAllDialogForType = (type: AddToAllDialogType, entityId: string) => {
        const expandedDisclosure = props.transfers.find((x) => x.id === getExpandedId());
        if (expandedDisclosure && expandedDisclosure.dataCategories.length > 1) {
            addToAllDialog.showDialogForType(type, entityId);
        }
    };

    const saveAllLegalBases = () => {
        const displayedType = addToAllDialog.getDisplayedType();
        const selectedEntityId = addToAllDialog.state.selectedEntityId;
        const expandedId = getExpandedId();

        if (!displayedType || !expandedId) {
            addToAllDialog.hideDialog(true);
            return;
        }

        const disclosure = props.transfers.find((x) => x.id === expandedId);
        if (!disclosure) {
            addToAllDialog.hideDialog(true);
            return;
        }

        disclosure.dataCategories.forEach((dc) => {
            switch (displayedType) {
                case "directive":
                    dc.directiveId = selectedEntityId;
                    break;
                case "nationalLaw":
                    dc.nationalLawId = selectedEntityId;
                    break;
                case "doubleLegalBasis":
                    dc.doubleLegalBasisId = selectedEntityId;
                    break;
            }
            props.onDisclosureUpdate(dc, expandedId);
        });

        addToAllDialog.hideDialog(true);
    };

    return {
        dataCategoryLoading,
        dataCategoriesData,
        legalEntityLoading,
        legalEntityData,
        responsibleLoading,
        groupEntityLoading,
        groupEntityData,
        transferBasisData,
        transferBasisLoading,
        legalBasesLoading,
        legalBasesData,
        agreementsLoading: documentsLoading || contractsOnDisclosureQuery.isLoading || selectableContractsQuery.isLoading,
        selectableAgreements: [...selectableDocuments, ...selectableContracts].sort((a, b) => a.name.localeCompare(b.name)),
        getResponsibles: responsibleQuery.data,
        dataProcessorAgreementSaveModel,
        setDataProcessorAgreementSaveModel,
        transferId,
        setTransferId,
        refetchDocuments: documentsQuery.refetch,
        getExpandedId,
        setExpandedId: (id: string) => {
            addToAllDialog.reset();
            setExpandedId(id);
        },
        onAddAgreement,
        onDeleteAgreement,
        onHasAgreementChange,
        onSaveNationalLaw,
        selectedAgreements: [
            ...(props.processingActivity.disclosures.find((d) => d.id === getExpandedId())?.transferAgreements ?? []),
            ...(contractsOnDisclosureQuery.data?.contractIds ?? []),
        ],
        containsNonAccessibleContacts: contractsOnDisclosureQuery.data?.containsNonAccessibleContacts ?? false,
        onLegalEntityOnTransferChange,
        showAddToAllDialogForType,
        saveAllLegalBases,
        hideAddToAllDialog: addToAllDialog.hideDialog,
        isAddToAllDialogVisible: addToAllDialog.isDialogVisible,
        getAddToAllDialogType: addToAllDialog.getDialogType,
    };

    async function addAgreementAPI(data: { processingActivityId: string; disclosureId: string; saveModel: AgreementSaveModel }) {
        return await put("/processingactivity/" + data.processingActivityId + "/disclosure/" + data.disclosureId + "/agreement", data.saveModel);
    }

    async function removeAgreementAPI(data: { processingActivityId: string; disclosureId: string; saveModel: AgreementSaveModel }) {
        return await deleteHttp(
            `/processingactivity/${data.processingActivityId}/disclosure/${data.disclosureId}/agreement?agreementId=${data.saveModel.agreementId}`
        );
    }

    async function addContractApi(data: { disclosureId: string; contractId: string }) {
        return await post(`/processingActivity/Disclosure/${data.disclosureId}/contracts/${data.contractId}`, null);
    }

    async function removeContractApi(data: { disclosureId: string; contractId: string }) {
        return await deleteHttp(`/processingActivity/Disclosure/${data.disclosureId}/contracts/${data.contractId}`);
    }
}

export function useValidateDisclosuresStep(permissions: ProcessingActivityDisclosuresPermissions) {
    const { translateString } = useTranslation();
    return (
        model: ProcessingActivityModel,
        legalEntities: Array<SelectableLegalUnitsItem> | undefined,
        sensitiveDataCategories: Array<string> | undefined,
        responsibles: Array<UserSelectableItem> | undefined,
        legalObligationId: string
    ): Array<ValidationError> => {
        return getTransfersErrors(
            model,
            translateString,
            legalEntities ?? [],
            sensitiveDataCategories ?? [],
            responsibles ?? [],
            legalObligationId,
            permissions
        );
    };
}

function getTransfersErrors(
    model: ProcessingActivityModel,
    translateString: any,
    legalEntities: Array<SelectableLegalUnitsItem>,
    sensitiveDataCategories: Array<string> | undefined,
    responsibles: Array<UserSelectableItem> | undefined,
    legalObligationId: string,
    permissions: ProcessingActivityDisclosuresPermissions
) {
    const result: Array<ValidationError> = [];

    if (permissions) {
        if (!model.disableDisclosures && !model.hasDisclosures) {
            result.push(new ValidationError("noDataSharingsDisclosures", translateString("atLeastOneSharingRequired")));
        }

        model.disclosures.forEach((transfer, index) => {
            if (!transfer.legalUnitId) {
                result.push(new ValidationError(`${index}legalEntityId`, translateString("noLegalUnitForTransferError")));
            }
            if (transfer.legalEntities.length === 0) {
                result.push(new ValidationError(`${index}companies`, translateString("noCompaniesForTransferChosenError")));
            }
            if (permissions.hasDataCategoriesPermission && transfer.dataCategories.length === 0) {
                result.push(new ValidationError(`${index}dataCategories`, translateString("dataCategoriesRequired")));
            }
            if (
                permissions.hasTransferBasisPermission &&
                legalEntities.some(
                    (t) =>
                        t.id === transfer.legalUnitId &&
                        (t.levelOfProtection === LevelOfProtectionType.ThirdCountry || t.levelOfProtection === LevelOfProtectionType.DataPrivacyFramework)
                ) &&
                !transfer.transferBasis
            ) {
                result.push(new ValidationError(`${index}transferBasisId`, translateString("transferBasisRequiredController")));
            }
            if (transfer.legalEntities.some((l) => l === transfer.legalUnitId)) {
                result.push(new ValidationError(`${index}legalEntityId`, translateString("legalUnitCantBeInCompanies")));
            }

            if (permissions.hasBasisOfDisclosurePermission) {
                transfer.dataCategories.forEach((d) => {
                    if (d.directiveId === legalObligationId && !d.nationalLawId) {
                        result.push(new ValidationError(`${index},${d.dataCategoryId},nationalLawId`, translateString("nationalLawRequired")));
                    }

                    if (!d.nationalLawId && !d.directiveId) {
                        result.push(new ValidationError(`${index},${d.dataCategoryId},directiveId`, translateString("legalBasisRequired")));
                    }
                    if (sensitiveDataCategories?.some((s) => s === d.dataCategoryId) && !d.doubleLegalBasisId) {
                        result.push(new ValidationError(`${index},${d.dataCategoryId},doubleLegalBasisId`, translateString("doubleLegalBasisRequired")));
                    }
                });
            }

            if (permissions.hasResponsiblePermission) {
                let user = responsibles?.find((x) => x.id === transfer.responsible);
                if (user && !user.active) {
                    result.push(new ValidationError(`${index}inactiveUser`, translateString("userDeactivated")));
                }
            }
        });
    }

    return result;
}
